import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Gem, Users, Eye, Package, Target, RefreshCw, Loader2, CalendarClock,
    AlertCircle, Search, ArrowUpDown, TrendingUp, TrendingDown, Radio,
    ShoppingCart, CheckCircle2, Clock, Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ChannelLink } from "@/components/ChannelLink";
import { premiumOverview, premiumBatches, activeAudience, premiumUpdates } from "@/lib/api";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const pct = (n) => (n == null ? "—" : `${n}%`);

/* ━━━━━━━━━━━━━━━━ KPI Card ━━━━━━━━━━━━━━━━ */
function Kpi({ label, value, sub, icon: Icon, color = "cyan", testId }) {
    const ring = {
        cyan: "ring-cyan-500/20 text-cyan-400",
        emerald: "ring-emerald-500/20 text-emerald-400",
        amber: "ring-amber-500/20 text-amber-400",
        rose: "ring-rose-500/20 text-rose-400",
        violet: "ring-violet-500/20 text-violet-400",
    }[color] || "ring-cyan-500/20 text-cyan-400";
    return (
        <Card className={`bg-card/50 ring-1 ${ring}`} data-testid={testId}>
            <CardContent className="p-4 flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-bold mt-1" dir="ltr" data-testid={`${testId}-value`}>
                        {typeof value === "number" ? fmt(value) : value}
                    </p>
                    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
                </div>
                {Icon && <Icon className="h-5 w-5 opacity-50 shrink-0" />}
            </CardContent>
        </Card>
    );
}

function MatchBadge({ value }) {
    if (value == null) return <span className="text-muted-foreground text-xs">—</span>;
    const cls = value >= 70
        ? "border-emerald-500/30 text-emerald-300"
        : value >= 40
            ? "border-amber-500/30 text-amber-300"
            : "border-rose-500/30 text-rose-300";
    return (
        <Badge variant="outline" className={`text-[11px] px-1.5 py-0 ${cls}`} dir="ltr">
            {value}%
        </Badge>
    );
}

/* ━━━━━━━━━━━━━━━━ Main Page ━━━━━━━━━━━━━━━━ */
export default function PremiumIntel() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [overview, setOverview] = useState(null);
    const [batches, setBatches] = useState(null);
    const [active, setActive] = useState(null);
    const [updates, setUpdates] = useState(null);
    const [query, setQuery] = useState("");
    const [sortKey, setSortKey] = useState("counted");
    const [sortDir, setSortDir] = useState("desc");

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [o, b, a, u] = await Promise.all([
                premiumOverview(),
                premiumBatches(),
                activeAudience(),
                premiumUpdates(),
            ]);
            setOverview(o);
            setBatches(b);
            setActive(a);
            setUpdates(u);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 403) setError("هذه الصفحة مخصّصة لمالك المنصّة فقط.");
            else setError(e?.response?.data?.detail || "تعذّر تحميل بيانات ذكاء البريميوم.");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const totals = overview?.totals || {};
    const upd = overview?.update || {};
    const bs = batches?.summary || overview?.batches_summary || {};
    const activeSummary = active?.summary || {};

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
        else { setSortKey(key); setSortDir("desc"); }
    };

    const channels = useMemo(() => {
        let rows = [...(overview?.channels || [])];
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            rows = rows.filter(
                (c) => (c.title || "").toLowerCase().includes(q) ||
                    (c.username || "").toLowerCase().includes(q),
            );
        }
        rows.sort((a, b) => {
            const av = a[sortKey] ?? -1;
            const bv = b[sortKey] ?? -1;
            return sortDir === "desc" ? bv - av : av - bv;
        });
        return rows;
    }, [overview, query, sortKey, sortDir]);

    const usernameById = useMemo(() => {
        const m = {};
        (overview?.channels || []).forEach((c) => { m[c.chat_id] = c.username; });
        return m;
    }, [overview]);

    const SortHead = ({ k, children, className = "" }) => (
        <TableHead
            className={`cursor-pointer select-none whitespace-nowrap ${className}`}
            onClick={() => toggleSort(k)}
            data-testid={`premium-sort-${k}`}
        >
            <span className="inline-flex items-center gap-1">
                {children}
                <ArrowUpDown className={`h-3 w-3 ${sortKey === k ? "text-primary" : "opacity-40"}`} />
            </span>
        </TableHead>
    );

    if (loading) {
        return (
            <div className="space-y-4 p-1" data-testid="premium-loading">
                <Skeleton className="h-24 w-full" />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-24" data-testid="premium-error">
                <Card className="bg-card/60 border-border/40 max-w-md w-full">
                    <CardContent className="p-8 text-center space-y-3">
                        <AlertCircle className="h-10 w-10 mx-auto text-rose-400" />
                        <p className="text-sm text-muted-foreground">{error}</p>
                        <Button variant="outline" onClick={load} className="gap-2" data-testid="premium-retry">
                            <RefreshCw className="h-4 w-4" /> إعادة المحاولة
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-5 pb-10" data-testid="premium-page">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <Gem className="h-6 w-6 text-amber-400" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold">ذكاء البريميوم</h1>
                        <p className="text-sm text-muted-foreground">
                            دفعات الشراء · نسبة التفعيل · الجمهور النشط · تنبؤ تحديث الترتيب
                        </p>
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="premium-refresh">
                    <RefreshCw className="h-4 w-4" /> تحديث
                </Button>
            </div>

            {/* Update prediction banner */}
            {upd?.last && (
                <Card className="bg-amber-500/5 border-amber-500/20" data-testid="premium-update-banner">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <CalendarClock className="h-4 w-4 text-amber-400" />
                            <span className="text-sm font-semibold text-amber-200">دورة تحديث الترتيب في تلغرام</span>
                            {upd.status && (
                                <Badge variant="outline" className="ml-auto border-amber-500/30 text-amber-300 text-xs">
                                    {upd.status}
                                </Badge>
                            )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                                <p className="text-[11px] text-muted-foreground">آخر تحديث</p>
                                <p className="font-bold mt-0.5" dir="ltr">{upd.last}</p>
                                <p className="text-[11px] text-muted-foreground">منذ {upd.days_since_last} يوم</p>
                            </div>
                            <div>
                                <p className="text-[11px] text-muted-foreground">التحديث القادم (متوقّع)</p>
                                <p className="font-bold mt-0.5 text-amber-300" dir="ltr">{upd.predict_median}</p>
                                <p className="text-[11px] text-muted-foreground" dir="ltr">أقصى: {upd.predict_max}</p>
                            </div>
                            <div>
                                <p className="text-[11px] text-muted-foreground">متوسط الدورة</p>
                                <p className="font-bold mt-0.5" dir="ltr">~{upd.median_gap} يوم</p>
                                <p className="text-[11px] text-muted-foreground" dir="ltr">أقصى: {upd.max_gap} يوم</p>
                            </div>
                            <div>
                                <p className="text-[11px] text-muted-foreground">الحالة</p>
                                <p className="font-bold mt-0.5">
                                    {upd.overdue ? <span className="text-rose-300">متأخّر</span>
                                        : upd.window_open ? <span className="text-amber-300">نافذة مفتوحة</span>
                                            : <span className="text-emerald-300">ضمن الدورة</span>}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Kpi label="محتسب (Boost) 💎" value={totals.counted} sub="ما يحتسبه تلغرام" icon={Gem} color="amber" testId="kpi-counted" />
                <Kpi label="حاضر فعلاً" value={totals.present_premium} sub="مميزون متتبَّعون" icon={Users} color="cyan" testId="kpi-present" />
                <Kpi label="إجمالي الأعضاء" value={totals.members} sub={`${totals.channels || 0} قناة`} icon={Radio} color="violet" testId="kpi-members" />
                <Kpi label="متوسط التفعيل" value={totals.avg_active_rate != null ? `${totals.avg_active_rate}%` : "—"} sub={`${totals.reliable_channels || 0} قناة موثوقة`} icon={Target} color="emerald" testId="kpi-active-rate" />
                <Kpi label="دفعات بانتظار التحديث" value={bs.pending_premium} sub={`${bs.pending || 0} دفعة · غادر ${fmt(bs.pending_left)}`} icon={Package} color="amber" testId="kpi-pending-batches" />
                <Kpi label="متوسط تطابق الفرضية" value={activeSummary.avg_hypothesis_match != null ? `${activeSummary.avg_hypothesis_match}%` : "—"} sub={`${activeSummary.channels || 0} قناة`} icon={Eye} color="cyan" testId="kpi-hypothesis" />
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-grid md:grid-cols-4" data-testid="premium-tabs">
                    <TabsTrigger value="overview" data-testid="tab-overview">نظرة عامة</TabsTrigger>
                    <TabsTrigger value="batches" data-testid="tab-batches">الدفعات</TabsTrigger>
                    <TabsTrigger value="active" data-testid="tab-active">الجمهور النشط</TabsTrigger>
                    <TabsTrigger value="updates" data-testid="tab-updates">سجل التحديثات</TabsTrigger>
                </TabsList>

                {/* ───── Overview tab: per-channel premium table ───── */}
                <TabsContent value="overview" className="mt-4">
                    <Card className="bg-card/40 border-border/40">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <CardTitle className="text-base">المميزون لكل قناة</CardTitle>
                                <div className="relative w-full sm:w-64">
                                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="بحث عن قناة…"
                                        className="pr-9"
                                        data-testid="premium-search"
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table data-testid="premium-overview-table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="whitespace-nowrap">القناة</TableHead>
                                            <SortHead k="counted" className="text-center">محتسب 💎</SortHead>
                                            <SortHead k="present_premium" className="text-center">حاضر</SortHead>
                                            <SortHead k="gap" className="text-center">الفجوة</SortHead>
                                            <SortHead k="active_rate" className="text-center">التفعيل</SortHead>
                                            <SortHead k="members" className="text-center">الأعضاء</SortHead>
                                            <SortHead k="premium_ratio" className="text-center">% مميز</SortHead>
                                            <TableHead className="text-center whitespace-nowrap">تغيّر منذ التحديث</TableHead>
                                            <SortHead k="hypothesis_match" className="text-center">تطابق</SortHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {channels.length === 0 && (
                                            <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">لا توجد بيانات.</TableCell></TableRow>
                                        )}
                                        {channels.map((c) => (
                                            <TableRow
                                                key={c.chat_id}
                                                className="cursor-pointer hover:bg-muted/30"
                                                onClick={() => navigate(`/premium/${c.chat_id}`)}
                                                data-testid={`premium-row-${c.chat_id}`}
                                            >
                                                <TableCell className="max-w-[200px]">
                                                    <ChannelLink title={c.title} username={c.username} chatId={c.chat_id} testId={`premium-channel-${c.chat_id}`} />
                                                </TableCell>
                                                <TableCell className="text-center font-bold text-amber-300" dir="ltr">{fmt(c.counted)}</TableCell>
                                                <TableCell className="text-center text-cyan-300" dir="ltr">{fmt(c.present_premium)}</TableCell>
                                                <TableCell className="text-center" dir="ltr">
                                                    {c.gap == null ? <span className="text-muted-foreground text-xs">قيد البناء</span>
                                                        : <span className="text-muted-foreground">{fmt(c.gap)}</span>}
                                                </TableCell>
                                                <TableCell className="text-center" dir="ltr">
                                                    {c.active_rate == null ? <span className="text-muted-foreground">—</span>
                                                        : <span className="text-emerald-300 font-medium">{c.active_rate}%</span>}
                                                </TableCell>
                                                <TableCell className="text-center text-muted-foreground" dir="ltr">{fmt(c.members)}</TableCell>
                                                <TableCell className="text-center text-muted-foreground" dir="ltr">{pct(c.premium_ratio)}</TableCell>
                                                <TableCell className="text-center" dir="ltr">
                                                    <span className="inline-flex items-center gap-2">
                                                        {c.joined_since_update ? <span className="text-emerald-300 inline-flex items-center"><TrendingUp className="h-3 w-3 mr-0.5" />{fmt(c.joined_since_update)}</span> : null}
                                                        {c.left_since_update ? <span className="text-rose-300 inline-flex items-center"><TrendingDown className="h-3 w-3 mr-0.5" />{fmt(c.left_since_update)}</span> : null}
                                                        {!c.joined_since_update && !c.left_since_update ? <span className="text-muted-foreground">—</span> : null}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-center"><MatchBadge value={c.hypothesis_match} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-3">
                                «الفجوة» = حاضر − محتسب (كم عضوًا مميزًا حاضرًا لم يحتسبه تلغرام بعد). «قيد البناء» = لم يكتمل تتبّع كل المميزين القدامى في هذه القناة بعد. اضغط على أي صف لفتح <b className="text-primary">تفاصيل البريميوم</b> للقناة، أو اضغط اسم القناة لفتحها في تلغرام.
                            </p>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ───── Batches tab ───── */}
                <TabsContent value="batches" className="mt-4">
                    <Card className="bg-card/40 border-border/40">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                <ShoppingCart className="h-4 w-4 text-amber-400" /> دفعات شراء المميزين
                                <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />مقيسة {bs.measured ?? 0}</Badge>
                                <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-xs"><Clock className="h-3 w-3 mr-1" />منتظرة {bs.pending ?? 0}</Badge>
                                {bs.avg_activation_pct != null && (
                                    <Badge variant="outline" className="border-cyan-500/30 text-cyan-300 text-xs">متوسط التفعيل {bs.avg_activation_pct}%</Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-xs text-muted-foreground mb-3">
                                «الدفعة» = مجموعة مميزين انضمّوا متقاربين زمنيًا (عملية شراء). «نسبة التفعيل» = كم منهم احتسبهم تلغرام فعليًا عند تحديث الترتيب — تُقاس مقابل <b>المتبقّي الحاضر</b> (من غادر لا يُحتسب ضدّك).
                            </p>
                            <div className="overflow-x-auto">
                                <Table data-testid="premium-batches-table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="whitespace-nowrap">القناة</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">التاريخ</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">شراء 🛒</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">متبقّي</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">غادر</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">عبر رابط</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">الحالة / التفعيل</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(!batches?.batches || batches.batches.length === 0) && (
                                            <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">لا توجد دفعات مكتشفة بعد.</TableCell></TableRow>
                                        )}
                                        {(batches?.batches || []).map((b, i) => (
                                            <TableRow key={i} data-testid={`batch-row-${i}`}>
                                                <TableCell className="max-w-[180px]"><ChannelLink title={b.title} username={usernameById[b.chat_id]} chatId={b.chat_id} showUsername={false} testId={`batch-channel-${i}`} /></TableCell>
                                                <TableCell className="text-center text-muted-foreground font-mono text-xs whitespace-nowrap" dir="ltr">{(b.start || "").slice(0, 10)}</TableCell>
                                                <TableCell className="text-center font-bold text-amber-300" dir="ltr">{fmt(b.size_premium)}</TableCell>
                                                <TableCell className="text-center text-cyan-300" dir="ltr">{fmt(b.present_premium)}</TableCell>
                                                <TableCell className="text-center" dir="ltr">{b.left_premium ? <span className="text-rose-300">{fmt(b.left_premium)}</span> : <span className="text-muted-foreground">0</span>}</TableCell>
                                                <TableCell className="text-center text-muted-foreground" dir="ltr">{Math.round((b.invite_ratio || 0) * 100)}%</TableCell>
                                                <TableCell className="text-center">
                                                    {b.status === "measured" ? (
                                                        <div className="flex flex-col items-center gap-1 min-w-[120px]">
                                                            <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-xs" dir="ltr">✅ {b.activation_pct}% ({fmt(b.counted_delta)})</Badge>
                                                            <Progress value={Math.min(100, b.activation_pct || 0)} className="h-1.5 w-full" />
                                                        </div>
                                                    ) : (
                                                        <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-xs"><Clock className="h-3 w-3 mr-1" />بانتظار التحديث</Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            {(bs.pending_premium_gross != null) && (
                                <p className="text-[11px] text-muted-foreground mt-3" dir="rtl">
                                    إجمالي الشراء (إجمالي): <b className="text-amber-300">{fmt(bs.pending_premium_gross)}</b> · المتبقّي الصافي بانتظار الاحتساب: <b className="text-cyan-300">{fmt(bs.pending_premium)}</b> · غادر من الدفعات المنتظرة: <b className="text-rose-300">{fmt(bs.pending_left)}</b>
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ───── Active audience tab ───── */}
                <TabsContent value="active" className="mt-4">
                    <Card className="bg-card/40 border-border/40">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                <Eye className="h-4 w-4 text-cyan-400" /> الجمهور النشط واختبار الفرضية
                                {activeSummary.avg_hypothesis_match != null && (
                                    <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-xs">متوسط التطابق {activeSummary.avg_hypothesis_match}%</Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-xs text-muted-foreground mb-3">
                                الفرضية: تلغرام يحتسب فقط المميزين ضمن «الجمهور النشط» (من يشاهد المنشورات). المتوقّع = حاضر × نسبة النشاط. كلما اقترب «المتوقّع» من «المحتسب» صحّت الفرضية.
                            </p>
                            <div className="overflow-x-auto">
                                <Table data-testid="premium-active-table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="whitespace-nowrap">القناة</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">👁 الجمهور النشط</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">نسبة النشاط</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">تفاعل/منشور</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">محتسب 💎</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">المتوقّع</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">التطابق</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(!active?.channels || active.channels.length === 0) && (
                                            <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">لا توجد بيانات جمهور نشط بعد.</TableCell></TableRow>
                                        )}
                                        {(active?.channels || []).map((c, i) => (
                                            <TableRow key={i} data-testid={`active-row-${i}`}>
                                                <TableCell className="max-w-[180px]">
                                                    <ChannelLink title={c.title} username={c.username || usernameById[c.chat_id]} chatId={c.chat_id} testId={`active-channel-${i}`} />
                                                </TableCell>
                                                <TableCell className="text-center font-bold text-cyan-300" dir="ltr">{fmt(c.active_audience)}</TableCell>
                                                <TableCell className="text-center text-muted-foreground" dir="ltr">{pct(c.active_ratio)}</TableCell>
                                                <TableCell className="text-center text-muted-foreground" dir="ltr">{fmt(c.reactions_per_post)}</TableCell>
                                                <TableCell className="text-center text-amber-300" dir="ltr">{fmt(c.counted)}</TableCell>
                                                <TableCell className="text-center text-muted-foreground" dir="ltr">{fmt(c.expected_counted)}</TableCell>
                                                <TableCell className="text-center"><MatchBadge value={c.hypothesis_match} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ───── Updates timeline tab ───── */}
                <TabsContent value="updates" className="mt-4">
                    <Card className="bg-card/40 border-border/40">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Activity className="h-4 w-4 text-amber-400" /> سجل تحديثات الترتيب
                                <Badge variant="outline" className="border-border/40 text-muted-foreground text-xs" dir="ltr">{updates?.count ?? 0}</Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-xs text-muted-foreground mb-4">
                                كل حدث هنا هو لحظة قام فيها تلغرام بتحديث عدد المميزين المحتسب (Boost). الفجوة بين الأحداث تساعد على التنبؤ بموعد التحديث القادم.
                            </p>
                            {(!updates?.events || updates.events.length === 0) ? (
                                <p className="text-sm text-muted-foreground text-center py-8">لا توجد أحداث تحديث مسجّلة بعد.</p>
                            ) : (
                                <div className="space-y-2">
                                    {[...updates.events].reverse().map((ev, i) => (
                                        <div key={i} className="flex items-center gap-3 rounded-lg bg-card/60 border border-border/30 p-3" data-testid={`update-row-${i}`}>
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/20 shrink-0">
                                                <CalendarClock className="h-4 w-4 text-amber-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium" dir="ltr">{ev.date}</p>
                                                {ev.note && <p className="text-[11px] text-muted-foreground truncate">{ev.note}</p>}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <Badge variant="outline" className="border-border/40 text-muted-foreground text-[10px]">{ev.source}</Badge>
                                                {ev.gap_days != null && (
                                                    <Badge variant="outline" className="border-cyan-500/20 text-cyan-300 text-[10px]" dir="ltr">+{ev.gap_days} يوم</Badge>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
